import Axios from 'axios';
import { getRecaptchaKey, getBffBaseUrl } from '../helper';
import type { EmpresasLeadBody } from './bffMappers';

/**
 * Cliente centralizado contra el BFF .NET.
 *
 * - Base URL: config.bff_url (react_config.json, vía getBffBaseUrl).
 * - Recaptcha v3: token FRESCO por cada llamada (single-use), enviado en el
 *   header `g-recaptcha-response` (NUNCA en el body).
 * - Cada función expone el shape de request/response tal cual lo entrega el BFF.
 */

const RECAPTCHA_HEADER = 'g-recaptcha-response';

// ---------- Tipos ----------

export interface BffContactoResponse {
  idContacto: string;
  token: string;
}

export interface BffPreciosRequest {
  compania: string;
  idContacto: string;
  token: string;
  emisionHabilitada: string;
}

/** Item de precios AP (el endpoint devuelve un ARRAY de estos). */
export interface ApPrecioItem {
  Id: number;
  Codigo: string;
  Descripcion: string;
  SumaAsegurada: number;
  Premio: number;
  NroCotizacion: string;
  Prima: number;
  Comision: number;
  Franquicia: string;
}

/** Item de precios Prepaga (van dentro de `items`). */
export interface PrepagaPrecioItem {
  codigoCobertura: string;
  descripcionCobertura: string;
  premio: string;
  columna: string;
  prima: number;
  franquicia: string;
  id: string;
  nroCotizacion: string;
  esPrecioPromocional: boolean;
}

/** Precios Prepaga: shape OBJETO (NO array), distinto de AP. */
export interface PrepagaPreciosResponse {
  sumaAsegurada: number;
  items: PrepagaPrecioItem[];
}

/**
 * Request del forwarder de lead de empresas (rubros solo-lead, sin cotización
 * online: ART, Caución, Celular, Comercio, landings genéricos).
 * Es el shape tipado §12.2 (lo arma el front con los mappers de bffMappers.ts).
 */
export type BffEmpresasLeadRequest = EmpresasLeadBody;

/**
 * Respuesta del forwarder a crmbridge. Es un ack (status ok); puede traer un
 * identificador del lead generado. Campos opcionales: el front no asume nada
 * más allá del ack.
 */
export interface BffEmpresasLeadResponse {
  idContacto?: string;
  token?: string;
}

// ---------- Helpers internos ----------

/** Mintea un token recaptcha v3 fresco (single-use) por cada invocación. */
const mintRecaptchaToken = async (): Promise<string> => {
  const key = await getRecaptchaKey();
  return new Promise<string>((resolve, reject) => {
    try {
      grecaptcha.ready(() => {
        grecaptcha
          .execute(key!, { action: 'submit' })
          .then((token: string) => resolve(token));
      });
    } catch (error) {
      reject(error);
    }
  });
};

/** POST tipado al BFF con token recaptcha fresco en el header. */
const postToBff = async <TReq, TRes>(
  path: string,
  body: TReq
): Promise<TRes> => {
  const captchaToken = await mintRecaptchaToken();
  const headers = {
    'content-type': 'application/json',
    [RECAPTCHA_HEADER]: captchaToken,
  };
  const baseUrl = await getBffBaseUrl();
  const response = await Axios.post<TRes>(baseUrl + path, body, {
    headers,
  });
  return response.data;
};

// ---------- Endpoints ----------

/** POST {BFF}/ap/contacto — body = DTO de cotización AP. */
export const apContacto = (
  dto: Record<string, unknown>
): Promise<BffContactoResponse> =>
  postToBff<Record<string, unknown>, BffContactoResponse>('/ap/contacto', dto);

/**
 * El BFF tipa PreciosRequestDTO con strings; el front recibe emisionHabilitada
 * como boolean (compania.emisionOnlineHabilitada). System.Text.Json NO convierte
 * bool->string y falla el bind del body entero (400). Forzamos strings acá.
 */
const normalizePreciosReq = (req: BffPreciosRequest): BffPreciosRequest => ({
  compania: String(req.compania ?? ''),
  idContacto: String(req.idContacto ?? ''),
  token: String(req.token ?? ''),
  emisionHabilitada: String(req.emisionHabilitada ?? 'false'),
});

/** POST {BFF}/ap/precios — devuelve un ARRAY de coberturas. */
export const apPrecios = (
  req: BffPreciosRequest
): Promise<ApPrecioItem[]> =>
  postToBff<BffPreciosRequest, ApPrecioItem[]>('/ap/precios', normalizePreciosReq(req));

/** POST {BFF}/prepaga/contacto — body = DTO contacto prepaga. */
export const prepagaContacto = (
  dto: Record<string, unknown>
): Promise<BffContactoResponse> =>
  postToBff<Record<string, unknown>, BffContactoResponse>(
    '/prepaga/contacto',
    dto
  );

/** POST {BFF}/prepaga/precios — devuelve OBJETO { sumaAsegurada, items[] }. */
export const prepagaPrecios = (
  req: BffPreciosRequest
): Promise<PrepagaPreciosResponse> =>
  postToBff<BffPreciosRequest, PrepagaPreciosResponse>('/prepaga/precios', normalizePreciosReq(req));

/**
 * POST {BFF}/empresas/lead — forwarder de lead a crmbridge para rubros
 * solo-lead (ART, Caución, Celular, Comercio, landings).
 * Recibe el body §12.2 ya armado (un solo arg) y lo postea tal cual. El BFF NO
 * coacciona tipos: el front es responsable de los tipos (ver bffMappers.ts).
 * El token recaptcha fresco va en el header `g-recaptcha-response`.
 */
export const empresasLead = (
  body: BffEmpresasLeadRequest
): Promise<BffEmpresasLeadResponse> =>
  postToBff<BffEmpresasLeadRequest, BffEmpresasLeadResponse>(
    '/empresas/lead',
    body
  );
